// src\lib\redux\executionFiles\executionFilesSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ExecutionFile } from "@/lib/redux/context";

interface ExecutionFilesState {
  data: ExecutionFile[] | null;
  counts: {
    quick_scout: number;
    steady_scout: number;
  };
  loading: boolean;
  error: string | null;
  meta: {
    total_items: number;
    total_pages: number;
    current_page: number;
    per_page: number;
    has_next: boolean;
    has_prev: boolean;
  } | null;
  links: {
    first: string | null;
    prev: string | null;
    next: string | null;
    last: string | null;
  } | null;
  countsLoading: boolean;
  countsError: string | null;
}

const initialState: ExecutionFilesState = {
  data: null,
  counts: {
    quick_scout: 0,
    steady_scout: 0,
  },
  loading: false,
  error: null,
  meta: null,
  links: null,
  countsLoading: false,
  countsError: null,
};

const executionFilesSlice = createSlice({
  name: "executionFiles",
  initialState,
  reducers: {
    // General loading state
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
      if (action.payload) state.error = null;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },

    // Counts loading state
    setCountsLoading(state, action: PayloadAction<boolean>) {
      state.countsLoading = action.payload;
      if (action.payload) state.countsError = null;
    },
    setCountsError(state, action: PayloadAction<string | null>) {
      state.countsError = action.payload;
    },

    // Data operations
    setExecutionFiles(
      state,
      action: PayloadAction<{
        items: ExecutionFile[];
        meta: ExecutionFilesState["meta"];
        links: ExecutionFilesState["links"];
      }>
    ) {
      state.data = action.payload.items;
      state.meta = action.payload.meta;
      state.links = action.payload.links;
    },

    addExecutionFile(state, action: PayloadAction<ExecutionFile>) {
      if (state.data) {
        state.data.push(action.payload);
      } else {
        state.data = [action.payload];
      }
    },

    updateExecutionFileStatus(
      state,
      action: PayloadAction<{ id: string; status: number }>
    ) {
      if (state.data) {
        const file = state.data.find((file) => file.id === action.payload.id);
        if (file) file.status = action.payload.status;
      }
    },

    updateExecutionFile(
      state,
      action: PayloadAction<{
        id: string;
        name: string;
        media_id: number;
        status: number;
        execution_type: string;
        start_time: string;
        end_time: string;
      }>
    ) {
      if (state.data) {
        const file = state.data.find((file) => file.id === action.payload.id);
        if (file) Object.assign(file, action.payload);
      }
    },

    removeExecutionFile(state, action: PayloadAction<{ id: string }>) {
      if (state.data) {
        state.data = state.data.filter((file) => file.id !== action.payload.id);
      }
    },

    setExecutionFilesCounts(
      state,
      action: PayloadAction<{
        quick_scout: number;
        steady_scout: number;
      }>
    ) {
      state.counts = action.payload;
    },
  },
});

export const executionFilesActions = executionFilesSlice.actions;
export default executionFilesSlice.reducer;